import { migrateScrollToOrigin } from "./migrateScroll.js";
import { BracketType, BracketTypeList, CANVAS_SIZE_MAX, CANVAS_SIZE_MIN, CANVAS_ZOOM_MAX, CANVAS_ZOOM_MIN, CanvasType, ColumnType, ColumnTypeList, Database, DatabaseList, InvertedLockSettings, Language, LanguageList, LockSettingFields, LockSettingType, LockSettingTypeList, NameCase, NameCaseList, NameViewMode, NameViewModeList, Show, isSettingLocked } from "../schema/settings.js";
import { assign, validNumber, validString } from "../../helper.js";
import { clamp, difference, isBoolean, isNil, isNumber, isPlainObject, isString, pick, uniq } from "es-toolkit";
//#region ../erd-editor-schema/dist/v3/parser/settings.js
var defaultShow = Show.tableComment | Show.columnComment | Show.columnDataType | Show.columnDefault | Show.columnPrimaryKey | Show.columnNotNull | Show.relationship;
var LOCK_ALL = LockSettingTypeList.reduce((acc, bit) => acc | bit, 0) & ~InvertedLockSettings;
var LOCKED_FIELDS = LockSettingTypeList.flatMap((bit) => LockSettingFields[bit]);
var toLockedValues = (settings) => pick(settings, LOCKED_FIELDS);
var DEFAULT_COLUMN_ORDER = [
	ColumnType.columnLogicalName,
	ColumnType.columnName,
	ColumnType.columnDataType,
	ColumnType.columnDomain,
	ColumnType.columnNotNull,
	ColumnType.columnUnique,
	ColumnType.columnAutoIncrement,
	ColumnType.columnDefault,
	ColumnType.columnComment
];
var createSettings = () => {
	const settings = {
		originX: 0,
		originY: 0,
		zoomLevel: 1,
		show: defaultShow,
		database: Database.MySQL,
		databaseName: "",
		canvasType: CanvasType.ERD,
		language: Language.GraphQL,
		tableNameCase: NameCase.pascalCase,
		columnNameCase: NameCase.camelCase,
		bracketType: BracketType.none,
		relationshipDataTypeSync: true,
		columnOrder: [...DEFAULT_COLUMN_ORDER],
		maxWidthComment: -1,
		lockSettings: LOCK_ALL,
		ddlScripts: {
			before: "",
			after: ""
		},
		nameViewMode: NameViewMode.physical
	};
	return {
		...settings,
		lockedValues: toLockedValues(settings)
	};
};
var isOrderOf = (columnTypes, order) => order.length === columnTypes.length && difference(columnTypes, order).length === 0;
var isKnownColumnType = (value) => ColumnTypeList.includes(value);
function indexOfHeld(order, columnTypes) {
	for (const columnType of columnTypes) {
		const index = order.indexOf(columnType);
		if (index !== -1) return index;
	}
	return -1;
}
function toFullColumnOrder(order) {
	if (!Array.isArray(order)) return null;
	if (isOrderOf(ColumnTypeList, order)) return order;
	const full = uniq(order.filter(isKnownColumnType));
	if (!full.length) return null;
	DEFAULT_COLUMN_ORDER.forEach((columnType, index) => {
		if (full.includes(columnType)) return;
		const before = indexOfHeld(full, DEFAULT_COLUMN_ORDER.slice(0, index).reverse());
		full.splice(before === -1 ? indexOfHeld(full, DEFAULT_COLUMN_ORDER.slice(index + 1)) : before + 1, 0, columnType);
	});
	return full;
}
function toSavedColumnOrder(order) {
	if (!Array.isArray(order)) return order;
	const seven = order.filter((columnType) => columnType !== ColumnType.columnLogicalName && columnType !== ColumnType.columnDomain);
	const full = toFullColumnOrder(seven);
	return full?.length === order.length && full.every((columnType, index) => columnType === order[index]) ? seven : order;
}
var LEGACY_CANVAS_SIZE = 2e3;
var isFiniteNumber = (value) => Number.isFinite(value);
var sizeInRange = (value) => clamp(value, CANVAS_SIZE_MIN, CANVAS_SIZE_MAX);
var zoomInRange = (value) => clamp(value, CANVAS_ZOOM_MIN, CANVAS_ZOOM_MAX);
var maxWidthCommentInRange = (value) => clamp(value, 60, 200);
function toLegacyScrollBox(json, zoomLevel) {
	const size = (value) => isNumber(value) ? sizeInRange(value) : LEGACY_CANVAS_SIZE;
	const scroll = (value) => isNumber(value) ? value : 0;
	return {
		width: size(json.width),
		height: size(json.height),
		zoomLevel,
		scrollLeft: scroll(json.scrollLeft),
		scrollTop: scroll(json.scrollTop)
	};
}
function createAndMergeSettings(json) {
	const settings = createSettings();
	if (!isPlainObject(json) || isNil(json)) return settings;
	const assignNumber = assign(isNumber, settings, json);
	const assignString = assign(isString, settings, json);
	const assignBoolean = assign(isBoolean, settings, json);
	if (isNumber(json.zoomLevel)) settings.zoomLevel = zoomInRange(json.zoomLevel);
	if (isNumber(json.maxWidthComment) && json.maxWidthComment !== -1) settings.maxWidthComment = maxWidthCommentInRange(json.maxWidthComment);
	assignNumber("show");
	assignString("databaseName");
	assignString("canvasType");
	assignBoolean("relationshipDataTypeSync");
	assign(validNumber(DatabaseList), settings, json)("database");
	assign(validNumber(LanguageList), settings, json)("language");
	assign(validNumber(NameCaseList), settings, json)("tableNameCase");
	assign(validNumber(NameCaseList), settings, json)("columnNameCase");
	assign(validNumber(BracketTypeList), settings, json)("bracketType");
	assign(validString(NameViewModeList), settings, json)("nameViewMode");
	settings.columnOrder = toFullColumnOrder(json.columnOrder) ?? settings.columnOrder;
	if (isFiniteNumber(json.originX) && isFiniteNumber(json.originY)) {
		settings.originX = json.originX;
		settings.originY = json.originY;
	} else {
		const { originX, originY } = migrateScrollToOrigin(toLegacyScrollBox(json, settings.zoomLevel));
		settings.originX = originX;
		settings.originY = originY;
	}
	if (isNumber(json.lockSettings)) {
		settings.lockSettings = json.lockSettings | 0;
		if (isSettingLocked(settings.lockSettings, LockSettingType.canvasType) && settings.canvasType === CanvasType.settings) settings.canvasType = CanvasType.ERD;
		settings.lockedValues = toLockedValues(settings);
	} else resetPreLockView(settings);
	const assignScript = assign(isString, settings.ddlScripts, json.ddlScripts);
	assignScript("before");
	assignScript("after");
	return settings;
}
function resetPreLockView(settings) {
	const { originX, originY, zoomLevel, canvasType } = createSettings();
	Object.assign(settings, {
		originX,
		originY,
		zoomLevel,
		canvasType
	});
	settings.lockedValues = toLockedValues(settings);
}
//#endregion
export { createAndMergeSettings, resetPreLockView, toFullColumnOrder, toLockedValues, toSavedColumnOrder };
